import { IrdEntitySchema } from '../metadata/metadata';
import { PagedData } from '../paged-data';
import { Promisified } from './async-mapper-factory';
import { DaoType, DefaultSyncDtoMapper, DtoType, SyncDtoMapper } from './sync-dto-mapper';

export interface DtoMapper<Dao extends DaoType, Dto extends DtoType> extends Promisified<SyncDtoMapper<Dao, Dto>> {
  sync: SyncDtoMapper<Dao, Dto>;
}

export abstract class DefaultDtoMapper<Dao extends DaoType, Dto extends DtoType> implements DtoMapper<Dao, Dto> {
  protected _sync: SyncDtoMapper<Dao, Dto>;

  public get sync(): SyncDtoMapper<Dao, Dto> {
    if (!this._sync) {
      this._sync = this.createSyncMapper();
    }
    return this._sync;
  }

  public createSyncMapper(): SyncDtoMapper<Dao, Dto> {
    return new DefaultSyncDtoMapper<Dao, Dto>(this.getDtoSample(), this.getDaoSample(), this.getSchema());
  }

  public fromDao(dao: Promise<Dao>): Promise<Dto>;

  public fromDao(dao: Promise<Dao | null>): Promise<Dto | null>;

  public fromDao(dao: Promise<Dao | null>): Promise<Dto | null> {
    return dao.then((awaitedDao) => {
      return this.sync.fromDao(awaitedDao);
    });
  }

  public fromDaos(daos: Promise<Dao[]>): Promise<Dto[]> {
    return daos.then((awaitedDaos) => {
      return this.sync.fromDaos(awaitedDaos);
    });
  }

  public toDao(dto: Promise<Dto>): Promise<Dao>;

  public toDao(dto: Promise<Dto | null>): Promise<Dao | null>;

  public toDao(dto: Promise<Dto | null>): Promise<Dao | null> {
    return dto.then((awaitedDto) => {
      return this.sync.toDao(awaitedDto);
    });
  }

  public async fromPagedDao(pagedData: Promise<PagedData<Dao>>, ...args: unknown[]): Promise<PagedData<Dto>> {
    const awaitedPagedData = await pagedData;
    return this.sync.fromPagedDao(awaitedPagedData, ...args);
  }

  protected abstract getDaoSample(): Dao;

  protected abstract getDtoSample(): Dto;

  /**
   * The DTO's metadata, where its fields carry backend names of their own. Mappers whose two
   * sides name every field alike need none, and map exactly as they did before.
   */
  protected getSchema(): IrdEntitySchema<Dto> | undefined {
    return undefined;
  }
}
